import { InfoModal } from "@newsela/angelou";
import { NextRouter } from "next/router";
import { useState } from "react";

import { getHasAssignees } from "@/assignments/utils/assignmentValidationHelpers/assignmentValidationHelpers";
import { Assignment } from "@/types/models";
import { UserProperties } from "@/types/models/UserProperties.interface";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import { GenerationGeniusContent } from "@/view/GeniusContent/GeniusContent.interface";
import ShareAssignmentModal from "@/view/ShareAssignmentModal";
import ShareAssignmentModalContent from "@/view/ShareAssignmentModal/ShareAssignmentModalContent";
import { getShareModalValues } from "@/view/ShareAssignmentModal/utils/getShareModalValues";
import { ASSIGNMENT_MODE } from "@/view/utils/currentAssignmentMode";
import { AssignModalProps } from "@/view/utils/useAssignmentReducerContext";
import { geniusAssignModalStyles } from "./GeniusAssignModal.style";
import GeniusAssignModalActions from "./GeniusAssignModalActions";
import GeniusAssignModalInputs from "./GeniusAssignModalInputs";

interface GeniusAssignModalProps extends AssignModalProps {
  assignmentMode: string;
  assignmentRequest: {
    data: Object;
    error: Error;
    loading: boolean;
  };
  content: GenerationGeniusContent;
  dispatch: () => void;
  handlePublish: () => any;
  handleSaveDraft: () => Promise<any>;
  router: NextRouter;
  shareModalProps: any;
  updatedAssignment: Assignment | undefined;
}

const GeniusAssignModal = ({
  assignmentMode,
  assignmentRequest,
  assignModalProps,
  content,
  dispatch,
  handlePublish,
  handleSaveDraft,
  router,
  setIsAssignModalActive,
  shareModalProps,
  updatedAssignment,
}: GeniusAssignModalProps) => {
  const selectedAssignment = assignmentRequest.data as Assignment;
  const userProperties = useGetUserProperties() as UserProperties;

  const [hasAssigneeError, setHasAssigneeError] = useState(false);

  const isCreateMode = assignmentMode === ASSIGNMENT_MODE.CREATE;
  const modalTitle = isCreateMode
    ? "Create your assignment"
    : "Assignment settings";

  const { selectedAssignmentId } = router.query;

  const { allAssignmentsLink, assignmentLink, isPrintDisabled, printLink } =
    getShareModalValues({
      content: { ...content, is_lite: content.data.is_lite },
      updatedAssignment: updatedAssignment as Assignment,
      userProperties,
    });

  const streamProperties = {
    content_type: "Assignment",
    content_id: content.id,
    object_id: updatedAssignment?.content_id || null,
  };

  const resetDate =
    updatedAssignment?.date_goes_live || selectedAssignment?.date_goes_live;

  if (!updatedAssignment) return null;

  const modalActions = (
    <GeniusAssignModalActions
      onClickCancel={() => setIsAssignModalActive(false)}
      onClickCreate={() => {
        setHasAssigneeError(!getHasAssignees(updatedAssignment));
        handlePublish();
      }}
      onClickSaveChanges={() => {
        setHasAssigneeError(!getHasAssignees(updatedAssignment));
        handlePublish();
      }}
      onClickSaveDraft={() => {
        handleSaveDraft();
        setIsAssignModalActive(false);
      }}
      updatedAssignment={updatedAssignment}
    />
  );

  return (
    <>
      <InfoModal
        {...assignModalProps}
        labelId={modalTitle}
        title={modalTitle}
        onBackgroundClick={() => setIsAssignModalActive(false)}
        __cssFor={geniusAssignModalStyles}
        footer={modalActions}
      >
        <GeniusAssignModalInputs
          assignmentRequest={assignmentRequest}
          content={content}
          dispatch={dispatch}
          handleSaveDraft={handleSaveDraft}
          hasAssigneeError={hasAssigneeError}
          resetDate={resetDate}
          selectedAssignmentId={selectedAssignmentId as string}
          setHasAssigneeError={setHasAssigneeError}
          updatedAssignment={updatedAssignment}
          userProperties={userProperties}
        />
      </InfoModal>

      <ShareAssignmentModal
        shareModalProps={shareModalProps}
        streamProperties={streamProperties}
        updatedAssignment={updatedAssignment}
      >
        <ShareAssignmentModalContent
          allAssignmentsLink={allAssignmentsLink}
          assignmentLink={assignmentLink}
          contentId={content?.id}
          isPrintDisabled={isPrintDisabled}
          printLink={printLink}
          streamProperties={streamProperties}
        />
      </ShareAssignmentModal>
    </>
  );
};

export default GeniusAssignModal;
